import { ChevronDown, Check, Building2 } from 'lucide-react'
import { useOrganization } from '@/contexts/OrganizationContext'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@atrvd/saas-ui'
import type { Organization } from '@/lib/supabase'

/**
 * Renders organization avatar with logo or fallback icon
 * Shows logo_url image if available, otherwise displays Building2 icon
 */
function OrganizationAvatar({
  organization,
  size = 'md',
}: {
  organization: Organization
  size?: 'sm' | 'md'
}) {
  const sizeClasses = size === 'sm' ? 'h-5 w-5' : 'h-6 w-6'

  if (organization.logo_url) {
    return (
      <Avatar className={sizeClasses}>
        <AvatarImage src={organization.logo_url} alt={organization.name} />
        <AvatarFallback>
          <Building2 className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} text-primary`} />
        </AvatarFallback>
      </Avatar>
    )
  }

  return <Building2 className={`${sizeClasses} text-primary`} />
}

/**
 * Organization switcher/display component
 *
 * For platform staff (super_admin, tech_team):
 * - Shows dropdown to switch between accessible organizations
 *
 * For regular users (admin, user, viewer):
 * - Shows organization name (read-only, no dropdown)
 *
 * Uses OrganizationContext for state management and persistence
 */
export function OrganizationSwitcher() {
  const {
    currentOrganization,
    accessibleOrganizations,
    setCurrentOrganization,
    canSwitchOrganizations: canSwitch,
    isLoadingOrganizations: isLoading,
  } = useOrganization()

  /**
   * Switch to selected organization
   * Updates context state and persists to database
   */
  const switchOrganization = (orgId: string) => {
    const newOrg = accessibleOrganizations.find(org => org.id === orgId)
    if (newOrg) {
      setCurrentOrganization(newOrg)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <Building2 className="h-5 w-5 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    )
  }

  if (!currentOrganization) {
    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <Building2 className="h-5 w-5 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">No organization</span>
      </div>
    )
  }

  // Regular users: show organization name without dropdown
  if (!canSwitch) {
    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <OrganizationAvatar organization={currentOrganization} />
        <span className="text-sm font-medium">{currentOrganization.name}</span>
      </div>
    )
  }

  // Platform staff: show dropdown to switch organizations
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center gap-2 h-auto py-2">
          <OrganizationAvatar organization={currentOrganization} />
          <span className="text-sm font-medium">{currentOrganization.name}</span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {accessibleOrganizations.map(org => (
          <DropdownMenuItem
            key={org.id}
            onClick={() => switchOrganization(org.id)}
            className="flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <OrganizationAvatar organization={org} size="sm" />
              <span>{org.name}</span>
            </div>
            {currentOrganization.id === org.id && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}